import React, { useState, useEffect, useRef } from 'react';
import { FiBell, FiCheck, FiInfo, FiCheckSquare } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { getNotifications, markAsRead, markAllAsRead } from './notificationService';
import { COLLECTION_NOTIFICATIONS } from '../shared/constants';
import './NotificationBell.css';

export default function NotificationBell({ userId, role }) {
    const [notifications, setNotifications] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();

    const unreadCount = notifications.filter(n => !n.read).length;

    useEffect(() => {
        if (!userId) return;

        const q = role === 'admin'
            ? query(
                collection(db, COLLECTION_NOTIFICATIONS),
                where('userId', 'in', [userId, 'global_admin']),
                where('role', '==', role),
                where('source', '==', 'guild'),
                orderBy('createdAt', 'desc'),
                limit(20)
            )
            : query(
                collection(db, COLLECTION_NOTIFICATIONS),
                where('userId', '==', userId),
                where('role', '==', role),
                orderBy('createdAt', 'desc'),
                limit(20)
            );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setNotifications(data);
        }, (error) => {
            console.error('NotificationBell Error:', error);
        });

        return () => unsubscribe();
    }, [userId, role]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    async function handleNotificationClick(n) {
        // Mark as read
        if (!n.read) {
            await markAsRead(n.id);
        }
        // Navigate to referral if referralId is present
        if (n.referralId) {
            setShowDropdown(false);
            navigate(`/referrals/${n.referralId}`);
        }
    }

    async function handleMarkRead(e, id) {
        e.stopPropagation(); // don't trigger the parent click
        await markAsRead(id);
    }

    async function handleMarkAllRead() {
        if (userId) {
            await markAllAsRead(userId, role);
        }
    }

    return (
        <div className="notification-wrapper" ref={dropdownRef}>
            <button
                className={`notification-btn ${unreadCount > 0 ? 'has-unread' : ''}`}
                onClick={() => setShowDropdown(!showDropdown)}
            >
                <FiBell />
                {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
            </button>

            {showDropdown && (
                <div className="notification-dropdown">
                    <div className="dropdown-header">
                        <div className="header-top">
                            <h3>Notifications</h3>
                            {unreadCount > 0 && <span>{unreadCount} new</span>}
                        </div>
                        {unreadCount > 0 && (
                            <button className="mark-all-btn" onClick={handleMarkAllRead}>
                                <FiCheckSquare /> Mark all as read
                            </button>
                        )}
                    </div>
                    <div className="notification-list">
                        {notifications.length > 0 ? (
                            notifications.map(n => (
                                <div
                                    key={n.id}
                                    className={`notification-item ${n.read ? 'read' : 'unread'} ${n.referralId ? 'clickable' : ''}`}
                                    onClick={() => handleNotificationClick(n)}
                                    title={n.referralId ? 'Click to view referral' : ''}
                                >
                                    <div className="notification-icon">
                                        <FiInfo />
                                    </div>
                                    <div className="notification-content">
                                        <p className="notif-title">{n.title}</p>
                                        <p className="notif-msg">{n.message}</p>
                                        <span className="notif-time">
                                            {n.createdAt?.toDate ? new Date(n.createdAt.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Just now'}
                                        </span>
                                    </div>
                                    {!n.read && (
                                        <button className="mark-read-btn" onClick={(e) => handleMarkRead(e, n.id)} title="Mark as read">
                                            <FiCheck />
                                        </button>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="empty-notifications">
                                <p>No notifications yet</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
